import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook to handle navigation warnings during evaluation completion
 * @param {Object} options - Configuration options
 * @param {boolean} options.isActive - Whether the warning should be active
 * @param {Object} options.evaluationData - Current evaluation data (answers, timeLeft, etc.)
 * @param {Function} options.onSaveProgress - Function to save progress before navigation
 * @param {boolean} options.isTimed - Whether the evaluation is timed
 * @param {Function} options.onSaveTimer - Function to save timer state
 */
const useNavigationWarning = ({
  isActive,
  evaluationData,
  onSaveProgress,
  isTimed,
  onSaveTimer
}) => {
  const navigate = useNavigate();
  const hasUnsavedChanges = useRef(false);
  const isNavigating = useRef(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Track if there are unsaved changes
  useEffect(() => {
    const hasAnswers = evaluationData?.answers && Object.keys(evaluationData.answers).length > 0;
    const hasComments = evaluationData?.comments && Object.keys(evaluationData.comments).length > 0;
    
    if (hasAnswers || hasComments) {
      hasUnsavedChanges.current = true;
    } else {
      hasUnsavedChanges.current = false;
    }
  }, [evaluationData?.answers, evaluationData?.comments]);

  // Handle browser navigation (back button, refresh, close tab)
  useEffect(() => {
    if (!isActive) return;

    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges.current) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    const handlePopState = (event) => {
      if (hasUnsavedChanges.current) {
        // Show our custom modal instead of allowing navigation
        setShowWarningModal(true);
        // Get user role to determine appropriate navigation path
        const userRole = localStorage.getItem('role');
        const defaultPath = userRole === 'resident' ? '/resident/evaluation' : 
                           userRole === 'attending' ? '/attending/evaluations' : 
                           userRole === 'director' ? '/evaluations' : '/dashboard';
        setPendingNavigation(defaultPath);
        
        // Push the current state back to prevent navigation
        setTimeout(() => {
          window.history.pushState(null, '', window.location.href);
        }, 0);
      }
    };

    // Add history state to track back button
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isActive]);

  // Additional effect to handle back button with a different approach
  useEffect(() => {
    if (!isActive) return;

    let lastUrl = window.location.href;
    
    const checkUrlChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl && hasUnsavedChanges.current) {
        setShowWarningModal(true);
        // Get user role to determine appropriate navigation path
        const userRole = localStorage.getItem('role');
        const defaultPath = userRole === 'resident' ? '/resident/evaluation' : 
                           userRole === 'attending' ? '/attending/evaluations' : 
                           userRole === 'director' ? '/evaluations' : '/dashboard';
        setPendingNavigation(defaultPath);
        
        // Restore the URL
        window.history.pushState(null, '', lastUrl);
      }
      lastUrl = currentUrl;
    };

    // Check for URL changes periodically
    const interval = setInterval(checkUrlChange, 100);
    
    return () => {
      clearInterval(interval);
    };
  }, [isActive]);

  // Handle programmatic navigation (React Router)
  const handleNavigation = useCallback(async (targetPath) => {
    if (!isActive || !hasUnsavedChanges.current) {
      isNavigating.current = true;
      navigate(targetPath);
      return;
    }

    // Show modal instead of simple confirm dialog
    setPendingNavigation(targetPath);
    setShowWarningModal(true);
  }, [isActive, navigate]);

  // Handle save and leave
  const handleSaveAndLeave = useCallback(async () => {
    try {
      // Save progress before navigating
      await onSaveProgress();
      
      // If it's a timed evaluation, save the timer state
      if (isTimed && onSaveTimer) {
        await onSaveTimer();
      }
      
      setShowWarningModal(false);
      isNavigating.current = true;
      navigate(pendingNavigation);
    } catch (error) {
      console.error('Error saving progress:', error);
      // Still navigate even if save fails
      setShowWarningModal(false);
      isNavigating.current = true;
      navigate(pendingNavigation);
    }
  }, [onSaveProgress, isTimed, onSaveTimer, navigate, pendingNavigation]);

  // Handle leave without saving
  const handleLeaveWithoutSaving = useCallback(async () => {
    // Save timer state even when leaving without saving answers
    if (isTimed && onSaveTimer) {
      try {
        await onSaveTimer();
      } catch (error) {
        console.error('Error saving timer state:', error);
        // Continue with navigation even if timer save fails
      }
    }
    
    setShowWarningModal(false);
    isNavigating.current = true;
    navigate(pendingNavigation);
  }, [navigate, pendingNavigation, isTimed, onSaveTimer]);

  // Handle cancel navigation
  const handleCancelNavigation = useCallback(() => {
    setShowWarningModal(false);
    setPendingNavigation(null);
  }, []);

  // Override navbar navigation
  useEffect(() => {
    if (!isActive) return;

    const handleNavbarClick = (event) => {
      // Check if the clicked element is a navigation link
      const navLink = event.target.closest('a[href]');
      if (!navLink) return;

      const href = navLink.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      // Prevent default navigation
      event.preventDefault();
      
      // Handle the navigation with warning
      handleNavigation(href);
    };

    // Add event listener to the document to catch all navigation clicks
    document.addEventListener('click', handleNavbarClick);
    
    return () => {
      document.removeEventListener('click', handleNavbarClick);
    };
  }, [isActive, handleNavigation]);

  // Reset navigation state when component unmounts
  useEffect(() => {
    return () => {
      isNavigating.current = false;
      hasUnsavedChanges.current = false;
    };
  }, []);

  return {
    handleNavigation,
    hasUnsavedChanges: hasUnsavedChanges.current,
    showWarningModal,
    handleSaveAndLeave,
    handleLeaveWithoutSaving,
    handleCancelNavigation,
    isTimed,
    timeLeft: evaluationData?.timeLeft || 0
  };
};

export default useNavigationWarning;
